<template>
  <LazyHydrate v-bind="hydrationStrategy">
    <!-- LazyHydrate doesn't add any element to the DOM, so we need to pick up the attributes here -->
    <ResponsiveWrapper
      v-bind="$attrs"
      :component="blok.component"
      :devices="blok.devices"
    >
      <SectionWrapper :blok="blok" padding="none">
        <div class="relative h-[450px] md:h-[400px] lg:h-[375px]">
          <div class="relative flex h-full w-full flex-col">
            <div class="absolute inset-0">
              <div
                class="absolute h-full w-full bg-cover bg-center bg-no-repeat md:max-h-full"
                :class="backgroundColorClass(blok.bgVariant || 'default')"
              >
                <img
                  v-if="company.coverPhotoUrl"
                  class="h-full w-full object-cover object-center"
                  alt=""
                  :src="img"
                  :srcset="imgSrcSet"
                  sizes="100vw"
                />
              </div>
              <WidthWrapper class="relative h-full py-5">
                <div
                  class="flex h-full w-full flex-col items-center justify-center md:items-start md:justify-end lg:flex-row lg:items-end"
                >
                  <div v-if="company.logoUrl" class="z-10 lg:translate-y-12">
                    <LogoBox
                      :options="logoOptions(140)"
                      class="p-5 lg:hidden"
                    />
                    <LogoBox
                      :options="logoOptions(180)"
                      class="hidden p-5 lg:flex"
                    />
                  </div>
                  <div
                    class="flex w-full flex-col md:flex-row md:items-end md:justify-between lg:ml-6"
                  >
                    <div
                      class="font-heading mt-4 text-center text-white md:text-left"
                    >
                      <h1
                        v-if="blok.title"
                        class="-mr-3 -ml-3 text-3xl leading-normal tracking-wide text-white sm:mr-0 sm:ml-0 sm:text-4xl lg:text-4xl"
                      >
                        <span
                          class="inline sm:leading-[3.25rem] xl:inline"
                          :class="[
                            company.coverPhotoUrl
                              ? 'bg-black/80 px-2 py-1'
                              : '',
                          ]"
                          >{{ blok.title }}</span
                        >
                      </h1>
                      <h2
                        v-if="blok.subtitle"
                        class="font-subheading mt-1 text-2xl leading-normal tracking-wide text-white sm:mt-2 sm:text-3xl md:text-3xl xl:mt-3"
                      >
                        <span
                          class="inline xl:inline"
                          :class="[
                            company.coverPhotoUrl
                              ? 'bg-black/80 px-2 py-1'
                              : '',
                          ]"
                          >{{ blok.subtitle }}</span
                        >
                      </h2>
                    </div>
                    <div
                      v-if="
                        blok.callToActionButtons &&
                        blok.callToActionButtons.length
                      "
                      class="mt-3 flex flex-col md:items-end md:justify-end"
                    >
                      <CallToActionButton
                        v-for="btn in blok.callToActionButtons"
                        :key="btn._uid"
                        :item="btn"
                        :blok="blok"
                        class="mx-auto mt-8 block w-full first:mt-0 sm:w-max md:mx-0 lg:mt-4"
                      />
                    </div>
                  </div>
                </div>
              </WidthWrapper>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </ResponsiveWrapper>
  </LazyHydrate>
</template>

<script setup>
import { computed } from 'vue'
import ResponsiveWrapper from '../components/ResponsiveWrapper.vue'
import LogoBox from '../components/LogoBox.vue'
import WidthWrapper from '../components/WidthWrapper.vue'
import SectionWrapper from '../components/SectionWrapper.vue'
import CallToActionButton from '../components/CallToActionButton.vue'
import { getImgSrc, getImgSrcset } from '../utils/imgUtils'
import { useHead } from '@unhead/vue'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const { backgroundColorClass } = useColorPalette()

const hydrationStrategy = computed(() => {
  if (props.blok.callToActionButtons?.length) {
    return {
      onInteraction: ['focus'],
    }
  } else {
    // If it doesn't have any button, it doesn't need hydration
    return { whenTriggered: false }
  }
})
const company = computed(() => {
  return props.context.company || { coverPhotoUrl: '' }
})
const img = computed(() => {
  return getImgSrc({
    imgUrl: company.value.coverPhotoUrl,
  })
})
const imgSrcSet = computed(() => {
  return getImgSrcset({
    imgUrl: company.value.coverPhotoUrl,
    dimensions: [
      { width: 640, height: 450 },
      { width: 1024, height: 450 },
      { width: 1920, height: 450 },
      { width: 2560, height: 450 },
    ],
    fit: 'crop',
    crop: 'faces',
  })
})

function logoOptions(width) {
  return {
    image: company.value?.logoUrl
      ? getImgSrc({
          imgUrl: company.value.logoUrl,
          trim: 'color',
          trimcolor: 'fff',
          width,
        })
      : null,
    title: props.blok.title,
    size: width,
  }
}

useHead(
  () => {
    if (props.context.company?.coverPhotoUrl) {
      return {
        link: [
          {
            rel: 'preload',
            as: 'image',
            href: img.value,
            imagesrcset: imgSrcSet.value,
            imagesizes: '100vw',
          },
        ],
      }
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>
